import { useMemo } from "react";
import { computeBreakdown, formatMoney } from "@/lib/calculations";
import { convert } from "@/lib/fx";
import { getCurrencySymbol } from "@/lib/constants";
import { ArrowRight, Receipt, TrendUp } from "@phosphor-icons/react";

export default function BreakdownPanel({ template, ratesObj, activeProductId }) {
  const product = template.products.find((p) => p.id === activeProductId);
  const fromCcy = template.inputCurrency;
  const toCcy = template.displayCurrency;
  const symbol = getCurrencySymbol(toCcy);

  const breakdown = useMemo(() => {
    if (!product) return null;
    return computeBreakdown(product, template.costLines, template.margin);
  }, [product, template]);

  const conv = (v) => convert(v, fromCcy, toCcy, ratesObj);

  return (
    <aside
      className="hidden xl:flex w-80 bg-white border-l border-neutral-300 flex-col"
      style={{ height: "calc(100vh - 60px)" }}
      data-testid="breakdown-panel"
    >
      <div className="p-4 border-b border-neutral-200 flex items-center gap-2">
        <Receipt size={16} weight="bold" className="text-[#002FA7]" />
        <h2 className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
          Price Breakdown
        </h2>
      </div>

      {!product || !breakdown ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <div className="w-16 h-16 bg-neutral-100 mb-4 flex items-center justify-center">
            <Receipt size={28} className="text-neutral-400" />
          </div>
          <p className="text-sm text-neutral-500">
            Select a product row to see its full cost-to-selling-price breakdown.
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 border-b border-neutral-200">
            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-400">
              Product
            </p>
            <p className="font-display font-bold text-lg mt-0.5 leading-tight" data-testid="breakdown-product-name">
              {product.name || "Untitled Product"}
            </p>
            <p className="text-xs text-neutral-500 mt-1 font-mono">
              {breakdown.quantity} × {fromCcy} {Number(product.cost).toFixed(2)}
              {fromCcy !== toCcy && (
                <>
                  <ArrowRight size={11} className="inline mx-1" /> {toCcy}
                </>
              )}
            </p>
          </div>

          <div className="px-4 py-3">
            <Row label={`Item cost (${fromCcy})`} value={formatMoney(breakdown.cost, getCurrencySymbol(fromCcy))} muted />
            {fromCcy !== toCcy && (
              <Row
                label={`Converted (${toCcy})`}
                value={formatMoney(conv(breakdown.cost), symbol)}
                muted
              />
            )}

            <div className="border-t border-dashed border-neutral-200 my-3" />

            {breakdown.lines.map((l) => (
              <Row
                key={l.id}
                label={
                  <span className="flex items-center gap-1.5">
                    <span>{l.label}</span>
                    {l.type === "percent" ? (
                      <span className="text-[10px] font-mono text-neutral-400 bg-neutral-100 px-1">
                        {l.value}%
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono text-neutral-400 bg-neutral-100 px-1">
                        flat
                      </span>
                    )}
                  </span>
                }
                value={`+ ${formatMoney(conv(l.amount), symbol)}`}
                testid={`breakdown-line-${l.id}`}
              />
            ))}

            <div className="border-t border-neutral-300 my-3" />

            <Row
              label="Landed cost"
              value={formatMoney(conv(breakdown.totalCostBeforeMargin), symbol)}
              bold
              testid="breakdown-landed"
            />

            <Row
              label={
                <span className="flex items-center gap-1.5">
                  <TrendUp size={12} className="text-emerald-600" />
                  Margin
                  <span className="text-[10px] font-mono text-neutral-400 bg-neutral-100 px-1">
                    {breakdown.marginPercent}%
                  </span>
                </span>
              }
              value={`+ ${formatMoney(conv(breakdown.marginAmount), symbol)}`}
              testid="breakdown-margin"
            />

            <div className="mt-4 bg-[#002FA7] text-white p-4">
              <p className="text-[10px] font-semibold tracking-[0.18em] uppercase opacity-70">
                Selling price (per unit)
              </p>
              <p
                className="font-mono font-bold text-3xl tracking-tight mt-1"
                data-testid="breakdown-selling-price"
              >
                {formatMoney(conv(breakdown.sellingPricePerUnit), symbol)}
              </p>
              {breakdown.quantity > 1 && (
                <div className="mt-3 pt-3 border-t border-white/20">
                  <p className="text-[10px] font-semibold tracking-[0.18em] uppercase opacity-70">
                    Total ({breakdown.quantity} units)
                  </p>
                  <p
                    className="font-mono font-bold text-xl mt-1"
                    data-testid="breakdown-total-selling"
                  >
                    {formatMoney(conv(breakdown.totalSellingPrice), symbol)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

function Row({ label, value, bold, muted, testid }) {
  return (
    <div
      className={`flex items-center justify-between py-1.5 text-sm ${
        bold ? "font-semibold" : ""
      } ${muted ? "text-neutral-500" : "text-neutral-900"}`}
      data-testid={testid}
    >
      <span className="text-xs">{label}</span>
      <span className={`font-mono ${bold ? "text-base" : ""}`}>{value}</span>
    </div>
  );
}
